/**
 * LessonStagePanel — generic wrapper for lesson pipeline stage UI.
 * Mirrors CourseStagePanel but uses LessonContext and lesson service methods.
 */

import React, { useCallback, useState } from 'react';
import { useLessonContext } from '../LessonContext';
import { LessonRunProgress } from './LessonRunProgress';
import { useArtifact } from '../../ContentManagementProject/shared/useArtifact';
import { DebugPanel } from '../../ContentManagementProject/shared/DebugPanel';
import { LessonPromptEditor } from './LessonPromptEditor';
import type { JobRecord } from '../../../services/jobsService';
import { courseRunsService } from '../../../services/courseRunsService';

export interface LessonStagePanelProps {
  stageId: string;
  title: string;
  description?: string;
  children?: React.ReactNode;
  renderOutput?: (document: Record<string, unknown>) => React.ReactNode;
  runDisabled?: boolean;
  runDisabledReason?: string;
  runLabel?: string;
  hideRunButton?: boolean;
  isLlmStage?: boolean;
  extractionId?: string;
}

export function LessonStagePanel({
  stageId,
  title,
  description,
  children,
  renderOutput,
  runDisabled,
  runDisabledReason,
  runLabel,
  hideRunButton,
  isLlmStage,
  extractionId,
}: LessonStagePanelProps) {
  const { document, lessonRun, executeStage, loading: docLoading, isExecuting } = useLessonContext();
  const runId = lessonRun?.run_id;

  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any[]>([]);
  const [jobError, setJobError] = useState<any>(null);

  const prompt = useArtifact(runId, stageId, courseRunsService.getLessonStagePrompt, !!isLlmStage, extractionId);
  const rawOutput = useArtifact(runId, stageId, courseRunsService.getLessonStageRaw, !!isLlmStage, extractionId);

  const isStageCompleted = (lessonRun?.stages || []).some(
    (s) => s.stage_id === stageId && s.status === 'succeeded'
  );

  const handleRunConfirmed = useCallback(async () => {
    setRunning(true);
    setJobStatus('queued');
    setJobProgress([]);
    setJobError(null);
    try {
      await executeStage(stageId, {
        onUpdate: (job: JobRecord) => {
          setJobStatus(job.status || null);
          if (Array.isArray(job.progress)) setJobProgress(job.progress);
        },
      });
      setJobStatus('succeeded');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Stage execution failed';
      setJobError(msg);
      setJobStatus('failed');
    } finally {
      setRunning(false);
    }
  }, [stageId, executeStage]);

  const handleRunClick = useCallback(() => {
    if (isStageCompleted) {
      setShowRerunConfirm(true);
    } else {
      handleRunConfirmed();
    }
  }, [isStageCompleted, handleRunConfirmed]);

  const externalJobRunning = isExecuting && !running;
  const isDisabled = runDisabled || running || docLoading || externalJobRunning;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>

      {externalJobRunning && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm text-amber-800">
          A job is currently running for this lesson. Please wait for it to complete.
        </div>
      )}

      {children}

      <div className="flex items-center gap-3">
        {!hideRunButton && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#1a2332' }}
            disabled={isDisabled}
            title={isDisabled ? (externalJobRunning ? 'Another job is running' : runDisabledReason || '') : undefined}
            onClick={handleRunClick}
          >
            {running ? 'Running...' : externalJobRunning ? 'Job in progress...' : (runLabel || `Run ${title}`)}
          </button>
        )}
        {!hideRunButton && jobStatus && jobStatus !== 'succeeded' && jobStatus !== 'failed' && (
          <span className="text-xs text-gray-500">{jobStatus}</span>
        )}
      </div>

      {(running || jobProgress.length > 0) && (
        <LessonRunProgress jobStatus={jobStatus} jobProgress={jobProgress} />
      )}

      {jobError && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          {typeof jobError === 'string' ? jobError : JSON.stringify(jobError)}
        </div>
      )}

      {document && renderOutput && (
        <div className="border-t border-gray-200 pt-4">
          {renderOutput(document)}
        </div>
      )}

      {/* Details panel */}
      <details className="mt-6 border border-gray-300 rounded-lg bg-gray-50">
        <summary className="px-4 py-3 text-sm font-semibold text-gray-500 cursor-pointer hover:text-gray-700 select-none">
          Details
        </summary>
        <div className="px-4 py-3 border-t border-gray-200 bg-white rounded-b-lg space-y-2">
          {isLlmStage && (
            <PromptEditorToggle stageId={stageId} extractionId={extractionId} />
          )}
          {isLlmStage && (
            <>
              {isStageCompleted ? (
                <details className="border border-gray-200 rounded" onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) prompt.load(); }}>
                  <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">Last Prompt Sent to LLM</summary>
                  <div className="px-3 py-2 border-t border-gray-100">
                    {prompt.loading && <div className="text-xs text-gray-400">Loading prompt...</div>}
                    {prompt.error && <div className="text-xs text-red-500">{prompt.error}</div>}
                    {prompt.data && <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-96 overflow-y-auto">{prompt.data}</pre>}
                  </div>
                </details>
              ) : (
                <div className="border border-gray-200 rounded px-3 py-2 text-xs text-gray-400">Last Prompt Sent to LLM — available after stage runs</div>
              )}

              {isStageCompleted ? (
                <details className="border border-gray-200 rounded" onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) rawOutput.load(); }}>
                  <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">LLM Raw Output</summary>
                  <div className="px-3 py-2 border-t border-gray-100">
                    {rawOutput.loading && <div className="text-xs text-gray-400">Loading output...</div>}
                    {rawOutput.error && <div className="text-xs text-red-500">{rawOutput.error}</div>}
                    {rawOutput.data && <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-96 overflow-y-auto">{rawOutput.data}</pre>}
                  </div>
                </details>
              ) : (
                <div className="border border-gray-200 rounded px-3 py-2 text-xs text-gray-400">LLM Raw Output — available after stage runs</div>
              )}
            </>
          )}

          <DebugPanel title={`Debug: ${title}`} details={{ stage_id: stageId, run_id: runId || null, job_status: jobStatus, job_error: jobError, job_progress: jobProgress }} />
        </div>
      </details>

      {showRerunConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowRerunConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Rerun this stage?</h3>
            <p className="text-sm text-gray-600">This will regenerate <strong>{title}</strong>. The previous output will be overwritten.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setShowRerunConfirm(false)}>Cancel</button>
              <button type="button" className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90" style={{ backgroundColor: '#1a2332' }} onClick={() => { setShowRerunConfirm(false); handleRunConfirmed(); }}>Rerun</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PromptEditorToggle({ stageId, extractionId }: { stageId: string; extractionId?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="border border-gray-200 rounded"
      onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) setOpen(true); }}
    >
      <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
        Prompt Editor
      </summary>
      <div className="px-3 py-2 border-t border-gray-100">
        {open ? <LessonPromptEditor stageId={stageId} extractionId={extractionId} /> : <div className="text-xs text-gray-400">Expand to load...</div>}
      </div>
    </details>
  );
}
